import React, { useCallback, useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import style from "./Monetization.module.css";
import { showToast } from "../../utils/Toastify/Toastify";
import {
  getWatchFraudLog,
  getWatchFraudLogDetail,
  resolveWatchFraudLog,
  confirmWatchFraudLog,
  confirmWatchFraudViewer,
  unconfirmWatchFraudLog,
  runWatchFraudScan,
} from "../../api/HttpApis";
import MonetizationFaq from "./MonetizationFaq";

const RULES = ["", "temporal_impossibility", "device_churn", "audience_concentration", "repeat_watch", "vpn_usage_pattern", "daily_volume_ceiling", "shared_ip_account_cluster", "implausible_seek_pattern"];
// Named after what the person appears to have DONE, not after the internal
// rule. "Device churn" and "audience concentration" mean nothing to someone
// who does not work on this system, and this list is read by people deciding
// whether to accuse a real customer of cheating.
const RULE_LABELS = {
  temporal_impossibility: "More hours than a day has",
  device_churn: "Too many new devices",
  audience_concentration: "Views from odd accounts",
  repeat_watch: "Same video over and over",
  vpn_usage_pattern: "Hiding their location",
  daily_volume_ceiling: "Too much watching in a day",
  shared_ip_account_cluster: "Many accounts, one place",
  implausible_seek_pattern: "Skipping faster than playing",
};
const chipClass = (rule) =>
  ({
    temporal_impossibility: style.chipFailed,
    device_churn: style.chipPending,
    audience_concentration: style.chipOn_hold,
    repeat_watch: style.chipPending,
    vpn_usage_pattern: style.chipPending,
    daily_volume_ceiling: style.chipPending,
    shared_ip_account_cluster: style.chipPending,
    implausible_seek_pattern: style.chipPending,
  }[rule] || style.chipHolding);

const who = (person) => {
  if (!person) return "—";
  return person.name || person.email || `#${person.id}`;
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");
const secs = (s) => {
  // Round to a whole second FIRST, then split — rounding the minutes and
  // seconds components independently (the previous version) double-counts
  // any value with a >=30s remainder: 400 became "7m 40s" instead of the
  // correct "6m 40s", because Math.round(400/60) rounds 6.67 up to 7 while
  // the seconds component separately renders the leftover 40s unchanged.
  const total = Math.round(s || 0);
  return `${Math.floor(total / 60)}m ${total % 60}s`;
};

// Plain-language summary of a finding's own detail JSON — the whole point
// of this panel is to let an admin see WHAT actually happened, not just a
// bare rule name.
function findingSummary(row) {
  const d = row.detail || {};
  switch (row.rule) {
    case "temporal_impossibility":
      return `Counted ${secs(d.credited_seconds)} of watching on ${d.day}, but only ${secs(d.elapsed_seconds)} actually passed — ${secs(d.excess_seconds)} more than the clock allows.`;
    case "device_churn": {
      // The machine count comes from the browser fingerprint, which survives
      // clearing storage and private windows — so "8 devices but 1 machine"
      // is one computer making up identities, not a big household. Older
      // findings have no machine count; stay quiet rather than imply one.
      const base = `Seen on ${d.distinct_device_count} different devices in ${d.window_hours} hours.`;
      if (d.id_cycling_trigger) {
        return `${base} But they are really only ${d.distinct_machine_count} ${d.distinct_machine_count === 1 ? "computer" : "computers"} — the same machine keeps making itself look new.`;
      }
      if (d.distinct_machine_count > 0) {
        return `${base} These do look like ${d.distinct_machine_count} genuinely different ${d.distinct_machine_count === 1 ? "computer" : "computers"}.`;
      }
      return base;
    }
    case "audience_concentration":
      if (d.age_blind_trigger) {
        return `This creator has only ${d.distinct_viewer_count} viewers in total, and ${d.exclusive_viewer_count} of them watch almost nothing else on the platform.`;
      }
      return `${Math.round((d.suspect_ratio || 0) * 100)}% of this creator's watch time comes from new accounts that watch almost nothing else.`;
    case "repeat_watch":
      return `Watched "${d.video_name || `#${d.video_meta_id}`}" all the way through ${d.completed_watch_count} times on ${d.day}.`;
    case "vpn_usage_pattern":
      return `${Math.round((d.flagged_ratio || 0) * 100)}% of this account's ${d.total_sessions} viewings in ${d.window_hours} hours hid where they were watching from.`;
    case "daily_volume_ceiling":
      return `Claimed ${secs(d.total_credited_seconds)} of watching on ${d.day} across all their devices — more than ${d.max_devices_setting} devices running all day could honestly produce.`;
    case "shared_ip_account_cluster":
      return `${d.distinct_account_count} separate accounts watched from the same internet connection in ${d.window_hours} hours, ${secs(d.total_credited_seconds)} in total.`;
    case "implausible_seek_pattern":
      return `Skipped forward ${d.suspicious_pair_count} times in one sitting, faster than the video could actually play — claiming ${secs(d.total_unexplained_seconds)} of watching that never happened.`;
    default:
      return "—";
  }
}

function EvidencePanel({ evidence, onExcludeViewer, busy }) {
  if (!evidence) return <p className={style.chartSub}>No underlying evidence available for this finding.</p>;

  if (evidence.type === "ledger_rows") {
    return (
      <table className={style.table}>
        <thead>
          <tr><th>Time</th><th>Creator</th><th>Video</th><th>Session</th><th>Credited</th></tr>
        </thead>
        <tbody>
          {evidence.rows.map((r, i) => (
            <tr key={i}>
              <td>{fmtDate(r.occurred_at)}</td>
              <td>#{r.creator_user_id}</td>
              <td>#{r.video_meta_id}</td>
              <td style={{ fontSize: 11 }}>{r.session_uuid}</td>
              <td>{secs(r.counted_seconds)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (evidence.type === "sessions") {
    // user_id is only present on Rule 7's evidence (the finding's subject
    // is the shared ip itself, not one account) — Rules 2/5 never include
    // it, since it's redundant with the finding's own subject there.
    const showAccount = evidence.rows.some((r) => r.user_id != null);
    return (
      <table className={style.table}>
        <thead>
          <tr>
            {showAccount && <th>Account</th>}
            <th>Started</th><th>Device</th><th>Type</th><th>IP</th>
            {!showAccount && <th>Fingerprint</th>}
          </tr>
        </thead>
        <tbody>
          {evidence.rows.map((r, i) => (
            <tr key={i}>
              {showAccount && <td>#{r.user_id}</td>}
              <td>{fmtDate(r.started_at)}</td>
              <td style={{ fontSize: 11 }}>{r.device_id || "—"}</td>
              <td>{r.device_type || "—"}</td>
              <td>{r.ip || "—"}</td>
              {!showAccount && <td style={{ fontSize: 11 }}>{r.client_fingerprint || "—"}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (evidence.type === "ledger_rows_with_position") {
    // Every position-bearing row for this session, across ANY video it
    // touched — an admin can see for themselves that a video-switch
    // boundary (a different "Video" value mid-list) was correctly excluded
    // from the pattern, rather than trusting that claim blind.
    return (
      <table className={style.table}>
        <thead>
          <tr><th>Time</th><th>Video</th><th>Seg #</th><th>Position</th><th>Credited</th></tr>
        </thead>
        <tbody>
          {evidence.rows.map((r, i) => (
            <tr key={i}>
              <td>{fmtDate(r.occurred_at)}</td>
              <td>#{r.video_meta_id}</td>
              <td>{r.segment_seq}</td>
              <td>{secs(r.position_seconds)}</td>
              <td>{secs(r.counted_seconds)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (evidence.type === "top_viewers") {
    // This rule flags a creator because of WHO watches them, so the useful
    // decision is per viewer, taken right here in the evidence table —
    // confirming the finding as a whole would hit genuine fans too.
    return (
      <table className={style.table}>
        <thead>
          <tr>
            <th>Viewer</th><th>Watch-time this period</th><th>Referred by this creator?</th>
            {/* Labelled, so the column reads as a decision to make rather than
                stray links beside the data. */}
            {onExcludeViewer && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {evidence.rows.map((r, i) => (
            <tr key={i}>
              <td>{who(r.viewer)}</td>
              <td>{secs(r.counted_seconds)}</td>
              <td>{r.referred_by_this_creator ? "Yes — excluded from suspect count" : "No"}</td>
              {onExcludeViewer && (
                <td style={{ whiteSpace: "nowrap" }}>
                  {r.viewer?.id != null && (
                    <button
                      className={`${style.btn} ${style.btnSm} ${style.btnDanger}`}
                      onClick={() => onExcludeViewer(r.viewer)}
                      disabled={busy}
                    >
                      Don't pay for this
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return null;
}

function DetailModal({ id, onClose, onResolved }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await getWatchFraudLogDetail(id);
        setData(res?.data ?? res ?? null);
      } catch (err) {
        showToast("Could not load this finding", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleResolve = async () => {
    setBusy(true);
    try {
      await resolveWatchFraudLog(id, { note });
      showToast("Marked as checked", "success");
      onResolved();
      onClose();
    } catch (err) {
      showToast("Could not save this", "error");
    } finally {
      setBusy(false);
    }
  };

  // The only action on this page that touches money. Kept behind its own
  // confirmation step with a required reason, because the person clicking it
  // is deciding that a real customer does not get paid for this viewing.
  const handleConfirm = async () => {
    setBusy(true);
    try {
      const res = await confirmWatchFraudLog(id, { reason: note });
      showToast(res?.message || "Confirmed — this watch time will not be paid for", "success");
      onResolved();
      onClose();
    } catch (err) {
      showToast(err?.response?.data?.message || "Could not confirm this", "error");
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  // Per-viewer exclusion, used by audience findings where confirming the
  // whole thing would also hit the creator's genuine fans. Reuses the note
  // box above as the required reason so there is one place to type.
  const handleExcludeViewer = async (viewer) => {
    const reason = note.trim();
    if (!reason) {
      showToast("Write what you found in the box below first — it is kept as the record of why", "error");
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Stop paying for ${viewer.name || `viewer #${viewer.id}`}'s watch time on this creator?\n\nYou can undo this later.`)) return;
    setBusy(true);
    try {
      const res = await confirmWatchFraudViewer(id, { viewer_user_id: viewer.id, reason });
      showToast(res?.message || "Done", "success");
      const fresh = await getWatchFraudLogDetail(id);
      setData(fresh?.data ?? fresh ?? null);
      onResolved();
    } catch (err) {
      showToast(err?.response?.data?.message || "Could not exclude that viewer", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleUndo = async () => {
    setBusy(true);
    try {
      const res = await unconfirmWatchFraudLog(id);
      showToast(res?.message || "Undone — this watch time counts again", "success");
      onResolved();
      onClose();
    } catch (err) {
      showToast(err?.response?.data?.message || "Could not undo this", "error");
    } finally {
      setBusy(false);
    }
  };

  // Findings that name no specific viewing (a shared connection, a creator's
  // audience pattern) cannot be acted on — the backend refuses them, so the
  // button is not offered rather than failing after the click.
  const d = data?.detail || {};
  const canConfirm =
    !!(d.session_uuids?.length || d.session_uuid) ||
    (d.rule === "daily_volume_ceiling" && d.day && data?.user_id != null);

  return (
    <div className={style.modalOverlay} onClick={onClose}>
      <div className={style.pdfModal} onClick={(e) => e.stopPropagation()} style={{ overflowY: "auto" }}>
        <button type="button" className={style.pdfModalClose} onClick={onClose} aria-label="Close">
          <FiX />
        </button>
        {loading || !data ? (
          <div className={style.loading}>Loading…</div>
        ) : (
          <>
            <h2 className={style.title} style={{ fontSize: 18 }}>
              {RULE_LABELS[data.detail?.rule] || data.detail?.rule}
            </h2>
            <p className={style.chartSub}>
              {data.creator_user_id != null ? "Creator" : "Viewer"}: {who(data.creator_user_id != null ? data.subject_creator : data.subject_user)}
            </p>
            <p className={style.chartSub} style={{ marginBottom: 16 }}>{findingSummary({ rule: data.detail?.rule, detail: data.detail })}</p>

            <h3 className={style.chartSub} style={{ fontWeight: 600, marginBottom: 8 }}>Underlying evidence</h3>

            {/* For audience findings the decision is made INSIDE the table
                below, one viewer at a time. That instruction has to come
                BEFORE the rows — underneath them it gets read last, or not
                at all, and the per-row buttons look like stray links. */}
            {data.evidence?.type === "top_viewers" && !data.confirmed_at && (
              <div className={style.noteBox} style={{ marginBottom: 12 }}>
                <strong>Decide one account at a time.</strong> This finding is about a creator's
                whole audience, so there is no single "it was all fake" button — that would also
                stop paying for genuine fans. Write what you found in the box below, then press{" "}
                <strong>"Don't pay for this"</strong> next to each account you believe is fake.
              </div>
            )}

            <div className={style.tableScroll}>
              <EvidencePanel
                evidence={data.evidence}
                busy={busy}
                onExcludeViewer={data.confirmed_at ? null : handleExcludeViewer}
              />
            </div>

            {data.confirmed_at ? (
              <div className={style.confirmedBox}>
                <strong>Confirmed as cheating</strong> on {fmtDate(data.confirmed_at)} by{" "}
                {who(data.confirmed_by)}.
                {data.excluded_row_count != null && (
                  <> {data.excluded_row_count} watch records no longer count toward payouts.</>
                )}
                {data.confirmed_reason ? <div style={{ marginTop: 6 }}>Reason: "{data.confirmed_reason}"</div> : null}
                <button className={style.linkBtn} onClick={handleUndo} disabled={busy}>
                  {busy ? "Undoing…" : "Undo this — count the watch time again"}
                </button>
              </div>
            ) : data.resolved_at ? (
              <div style={{ marginTop: 16, fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
                Checked {fmtDate(data.resolved_at)} by {who(data.resolved_by)}
                {data.review_note ? ` — "${data.review_note}"` : ""}
              </div>
            ) : (
              <div style={{ marginTop: 16 }}>
                <textarea
                  className={style.noteInput}
                  placeholder="What did you find? e.g. one machine using nine device IDs — or: looks like a real family, nothing wrong"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />

                {confirming ? (
                  <div className={style.confirmWarn}>
                    <strong>This stops money.</strong> The watch time in this finding will no
                    longer count toward anyone's payout. You can undo it later.
                    <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                      <button className={`${style.btn} ${style.btnSm}`} onClick={handleConfirm} disabled={busy || !note.trim()}>
                        {busy ? "Confirming…" : "Yes, stop paying for this"}
                      </button>
                      <button className={`${style.btn} ${style.btnGhost} ${style.btnSm}`} onClick={() => setConfirming(false)} disabled={busy}>
                        Cancel
                      </button>
                    </div>
                    {!note.trim() && (
                      <div style={{ marginTop: 8, fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
                        Please write above what you found first — it is kept as the record of why.
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* When a finding names no specific viewing, say so HERE —
                        in place of the missing button, not as a footnote under
                        it. Otherwise the panel just looks broken. */}
                    {/* The audience case is explained above the table, where
                        the buttons actually are. Only the shared-connection
                        case needs a word here, since it has no per-row
                        action at all. */}
                    {!canConfirm && d.rule === "shared_ip_account_cluster" && (
                      <div className={style.noteBox} style={{ marginTop: 12, marginBottom: 0 }}>
                        <strong>Nothing here can be stopped from this screen.</strong> Several
                        unrelated accounts share this internet connection — and on mobile networks
                        thousands of strangers often do. Open those accounts on their own and judge
                        each one.
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                      <button className={`${style.btn} ${style.btnGhost} ${style.btnSm}`} onClick={handleResolve} disabled={busy}>
                        {busy ? "Saving…" : "Looks fine — mark as checked"}
                      </button>
                      {canConfirm && (
                        <button className={`${style.btn} ${style.btnSm} ${style.btnDanger}`} onClick={() => setConfirming(true)} disabled={busy}>
                          This was cheating — stop paying for it
                        </button>
                      )}
                    </div>
                  </>
                )}

              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function WatchFraudLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("unresolved");
  const [rule, setRule] = useState("");
  const [scanBusy, setScanBusy] = useState(false);
  const [detailId, setDetailId] = useState(null);
  // The questions this FAQ answers ("does resolving this take their money
  // back?") get asked while looking at THIS page, not while in Settings — so
  // the same content is reachable from here too.
  const [showFaq, setShowFaq] = useState(false);
  const limit = 50;

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWatchFraudLog({ page, limit, status, rule });
      const payload = res?.data ?? res ?? {};
      setRows(Array.isArray(payload.data) ? payload.data : []);
      setTotal(payload.total || 0);
    } catch (err) {
      showToast("Failed to load the suspected-activity log", "error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, status, rule]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleScan = async () => {
    setScanBusy(true);
    try {
      const res = await runWatchFraudScan();
      const out = res?.data ?? res ?? {};
      showToast(`Scan complete — ${out.written || 0} new finding(s).`, "success");
      fetchRows();
    } catch (err) {
      showToast("Scan failed", "error");
    } finally {
      setScanBusy(false);
    }
  };

  const lastPage = Math.max(1, Math.ceil(total / limit));

  return (
    <div className={style.page}>
      <div className={style.headerRow}>
        <div>
          <h2 className={style.title}>Suspected Activity</h2>
          <p className={style.subtitle}>
            Accounts worth a closer look, and what they did. Open one to see the evidence, then
            either mark it as fine or stop paying for that watch time.{" "}
            <button type="button" className={style.linkBtn} style={{ marginTop: 0 }} onClick={() => setShowFaq(true)}>
              How does this work?
            </button>
          </p>
        </div>
        <button className={`${style.btn} ${style.btnGhost}`} onClick={handleScan} disabled={scanBusy}>
          {scanBusy ? "Scanning…" : "Run Scan Now"}
        </button>
      </div>

      <div className={style.toolbar}>
        <select className={style.select} style={{ width: 160 }} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="unresolved">Unresolved</option>
          <option value="resolved">Resolved</option>
          <option value="">All</option>
        </select>
        <select className={style.select} style={{ width: 220 }} value={rule} onChange={(e) => { setRule(e.target.value); setPage(1); }}>
          {RULES.map((r) => (
            <option key={r} value={r}>{r ? RULE_LABELS[r] : "All rules"}</option>
          ))}
        </select>
      </div>

      <div className={style.tableWrap}>
        <table className={style.table}>
          <thead>
            <tr>
              <th>Flagged</th>
              <th>Rule</th>
              <th>Who</th>
              <th>What happened</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className={style.loading}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className={style.empty}>Nothing flagged.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td>{fmtDate(r.created_at)}</td>
                  <td><span className={`${style.chip} ${chipClass(r.rule)}`}>{RULE_LABELS[r.rule] || r.rule}</span></td>
                  <td>
                    {who(r.subject_creator || r.subject_user)}
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>{r.subject_creator ? "creator" : "viewer"}</div>
                  </td>
                  <td style={{ maxWidth: 420 }}>{findingSummary(r)}</td>
                  <td>
                    {r.resolved_at ? (
                      <span className={`${style.chip} ${style.chipPaid}`}>Resolved</span>
                    ) : (
                      <span className={`${style.chip} ${style.chipPending}`}>Open</span>
                    )}
                  </td>
                  <td>
                    <button className={`${style.btn} ${style.btnGhost} ${style.btnSm}`} onClick={() => setDetailId(r.id)}>
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > limit && (
        <div className={style.toolbar} style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <button className={`${style.btn} ${style.btnGhost} ${style.btnSm}`} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </button>
          <span style={{ alignSelf: "center", fontSize: 13 }}>Page {page} of {lastPage}</span>
          <button className={`${style.btn} ${style.btnGhost} ${style.btnSm}`} disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}

      {detailId != null && (
        <DetailModal id={detailId} onClose={() => setDetailId(null)} onResolved={fetchRows} />
      )}

      {showFaq && (
        <div className={style.modalOverlay} onClick={() => setShowFaq(false)}>
          <div className={style.pdfModal} onClick={(e) => e.stopPropagation()} style={{ overflowY: "auto" }}>
            <button type="button" className={style.pdfModalClose} onClick={() => setShowFaq(false)} aria-label="Close">
              <FiX />
            </button>
            <h2 className={style.title} style={{ fontSize: 18 }}>How this works</h2>
            <MonetizationFaq />
          </div>
        </div>
      )}
    </div>
  );
}
